import React from 'react';

// Deploy trigger (no-op): bump to roll frontend build — remove anytime.

import { GoogleOAuthProvider } from '@react-oauth/google';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import { AppInitializer } from './components/atoms/app-initializer';
import { ErrorBoundaryFallback } from './components/atoms/error-boundary';
import { RouteAnalytics } from './components/atoms/route-analytics';
import { NotificationProvider } from './components/molecules/notification-context';
import MainRoutes from './MainRoutes';
import { store } from './store';
import { Theme } from './theme';
import { initGA } from './utils/analytics';
import { initSentry, Sentry } from './utils/sentry';

import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'quill/dist/quill.snow.css';

import './theme/styles/style.scss';
import './theme/styles/antd-overrides.scss';

initSentry();
initGA();

if (import.meta.env.DEV) {
  const copyText = (text) => {
    navigator.clipboard.writeText(text).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;left:-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    });
  };

  const showOverlay = (title, detail) => {
    let box = document.getElementById('__global-error-overlay');

    if (!box) {
      box = document.createElement('div');
      box.id = '__global-error-overlay';
      Object.assign(box.style, {
        position: 'fixed',
        bottom: '0',
        left: '0',
        right: '0',
        zIndex: '99998',
        background: '#1a1a2e',
        color: '#ff6b6b',
        fontFamily: 'monospace',
        fontSize: '12px',
        padding: '12px 16px',
        maxHeight: '40vh',
        overflow: 'auto',
        borderTop: '2px solid #ff4d4f',
      });
      document.body.appendChild(box);
    }
    const entry = document.createElement('div');
    entry.style.marginBottom = '8px';
    const fullText = `${title}\n${detail}\nPage: ${window.location.pathname}\nUA: ${navigator.userAgent}`;
    entry.innerHTML = `<strong style="color:#ff4d4f">${title}</strong><pre style="margin:4px 0 0;white-space:pre-wrap;word-break:break-word">${detail}</pre>`;
    const btn = document.createElement('button');
    btn.textContent = 'Copy';
    Object.assign(btn.style, {
      marginTop: '6px',
      padding: '4px 12px',
      borderRadius: '4px',
      border: '1px solid #1890ff',
      background: 'transparent',
      color: '#1890ff',
      fontWeight: '600',
      fontSize: '12px',
      cursor: 'pointer',
    });
    btn.onclick = () => {
      copyText(fullText);
      btn.textContent = 'Copied!';
      btn.style.color = '#52c41a';
      btn.style.borderColor = '#52c41a';
    };
    entry.appendChild(btn);
    box.appendChild(entry);
  };

  window.addEventListener('error', (e) => {
    showOverlay('Uncaught Error', `${e.message}\n${e.filename}:${e.lineno}:${e.colno}`);
  });

  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason;
    const msg = reason instanceof Error ? `${reason.message}\n${reason.stack}` : String(reason);
    showOverlay('Unhandled Promise Rejection', msg);
  });
}

const app = (
  <React.StrictMode>
    <HelmetProvider>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <Provider store={store}>
          <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
            <NotificationProvider>
              <Theme>
                <Sentry.ErrorBoundary fallback={ErrorBoundaryFallback} showDialog={false}>
                  <AppInitializer>
                    <RouteAnalytics />
                    <MainRoutes />
                  </AppInitializer>
                </Sentry.ErrorBoundary>
              </Theme>
            </NotificationProvider>
          </GoogleOAuthProvider>
        </Provider>
      </BrowserRouter>
    </HelmetProvider>
  </React.StrictMode>
);

const rootEl = document.getElementById('root');
const ssrPath = rootEl.getAttribute('data-ssr-path');
const canHydrate = ssrPath && !rootEl.querySelector('#app-loader') && ssrPath === window.location.pathname;

if (canHydrate) {
  ReactDOM.hydrateRoot(rootEl, app, {
    onRecoverableError: (error) => {
      if (import.meta.env.DEV) console.warn('Hydration mismatch:', error);
    },
  });
} else {
  rootEl.removeAttribute('data-ssr-path');
  ReactDOM.createRoot(rootEl).render(app);
}
